import { AuditLog } from '@/shared/types/auditLogs';
import { type DateBucket, DATE_BUCKET_ORDER, getDateBucket } from '@/util/dateUtils';
import { hasAuditLogDiff } from '@/util/diffUtils';

// ── Shared fields computation ──────────────────────────────────────────
// When a group is collapsed into a single header row, each field is shown
// only when every member agrees on it; divergent fields collapse to a
// neutral default so the header never claims false unanimity.
export type SharedFields = Partial<
  Pick<
    AuditLog,
    'source_id' | 'source_name' | 'source_type' | 'verb' | 'target_name' | 'brain_id' | 'desk_id'
  >
>;

export const getSharedFields = (activities: AuditLog[]): SharedFields => {
  if (!activities.length) {
    return {
      source_id: null,
      source_name: null,
      source_type: null,
      verb: 'updated',
      target_name: null,
      brain_id: null,
      desk_id: null,
    };
  }
  const first = activities[0];
  // A system actor is its source token: each write carries a fresh source_id, so the header drops it.
  const isSystem = first.source_type === 'system';
  const sameActor = isSystem
    ? activities.every((a) => a.source_type === 'system' && a.source_name === first.source_name)
    : activities.every((a) => a.source_id === first.source_id);
  return {
    source_id: sameActor && !isSystem ? first.source_id : null,
    source_name: sameActor ? first.source_name : null,
    source_type: sameActor ? first.source_type : null,
    verb: activities.every((a) => a.verb === first.verb) ? first.verb : 'updated',
    target_name: activities.every((a) => a.target_name === first.target_name)
      ? first.target_name
      : activities[0].target_name,
    brain_id: activities.every((a) => a.brain_id === first.brain_id) ? first.brain_id : null,
    desk_id: activities.every((a) => a.desk_id === first.desk_id) ? first.desk_id : null,
  };
};

// New-account and invite-response events are hidden; filtered before grouping so they don't leave an empty row.
export const isRenderableActivityLog = (log: AuditLog): boolean =>
  !(log.verb === 'created' && log.target_type === 'accounts') && log.verb !== 'responded';

// ── Grouping logic ─────────────────────────────────────────────────────
export type GroupedLog = AuditLog & { nested: AuditLog[] };

const EMPTY_GROUPED_LOG: GroupedLog = {
  log_id: '',
  account_id: '',
  source_id: null,
  source_name: null,
  source_type: null,
  target_id: '',
  target_name: null,
  target_type: 'accounts',
  extra: {},
  log_type: 'audit',
  verb: 'updated',
  brain_id: null,
  desk_id: null,
  created: '',
  nested: [],
};

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

const toGroupedLog = (run: AuditLog[]): GroupedLog => {
  if (run.length === 1) {
    return { ...run[0], nested: [] };
  }
  return {
    ...EMPTY_GROUPED_LOG,
    log_id: run[0].log_id,
    target_id: run[0].target_id,
    target_type: run[0].target_type,
    account_id: run[0].account_id,
    created: run[0].created,
    ...getSharedFields(run),
    nested: run,
  };
};

/** Merges consecutive events on (source_id, target_id) — system events on (source_name, target_id) — within a sliding 6h window, newest first. */
export const groupActivityLogs = (actions: AuditLog[]): GroupedLog[] => {
  const renderable = actions.filter(isRenderableActivityLog);
  if (!renderable.length) {
    return [];
  }

  const sorted = [...renderable].sort((a, b) => {
    const timeDiff = new Date(b.created).getTime() - new Date(a.created).getTime();
    if (timeDiff !== 0) {
      return timeDiff;
    }
    if (a.target_id !== b.target_id) {
      return a.target_id < b.target_id ? -1 : 1;
    }
    return a.log_id < b.log_id ? -1 : a.log_id > b.log_id ? 1 : 0;
  });

  const result: GroupedLog[] = [];
  let run: AuditLog[] = [];

  const flush = () => {
    if (run.length) {
      result.push(toGroupedLog(run));
      run = [];
    }
  };

  const canGroup = (log: AuditLog) => log.source_type === 'system' || hasAuditLogDiff(log.extra);

  const getRunKey = (log: AuditLog) =>
    log.source_type === 'system'
      ? `system:${log.source_name}:${log.target_id}`
      : `${log.source_id}:${log.target_id}`;

  for (const log of sorted) {
    const prev = run[run.length - 1];
    const belongsToRun =
      prev !== undefined &&
      canGroup(prev) &&
      canGroup(log) &&
      getRunKey(prev) === getRunKey(log) &&
      Math.abs(new Date(prev.created).getTime() - new Date(log.created).getTime()) <= SIX_HOURS_MS;

    if (!belongsToRun) {
      flush();
    }
    run.push(log);
  }
  flush();

  return result;
};

// ── Day buckets ────────────────────────────────────────────────────────
// Calendar-day bucketing lives in util/dateUtils (shared with the contacts
// activity tab); this just maps groups into those buckets by their latest event.
export interface ActivityBucket {
  key: DateBucket;
  groups: GroupedLog[];
}

/**
 * Assigns each group to a day bucket by its latest event (`created`), never
 * splitting a group across buckets. Empty buckets are omitted. Input is assumed
 * newest-first (as returned by `groupActivityLogs`), so bucket order and
 * within-bucket order are preserved.
 */
export const bucketActivityGroups = (
  groups: GroupedLog[],
  now: number = Date.now()
): ActivityBucket[] => {
  const byKey: Record<DateBucket, GroupedLog[]> = {
    today: [],
    yesterday: [],
    last_7_days: [],
    last_30_days: [],
    older: [],
  };

  for (const group of groups) {
    byKey[getDateBucket(group.created, now)].push(group);
  }

  return DATE_BUCKET_ORDER.map((key) => ({ key, groups: byKey[key] })).filter(
    (bucket) => bucket.groups.length > 0
  );
};
